// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import React, { FunctionComponent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// xng-change
const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const currentMode = getCookie("NavigationMode") || "Storefront";

    return {
        cart,
        isPunchOutOrder: isPunchOutOrder(cart),
        currentMode,
        isAuthenticated: xIsAuthenticated(state),
        isGuest: state.context.session?.isGuest,
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // xng-change
    isPunchOutOrder,
    currentMode,
    isAuthenticated,
    isGuest,
    hasInvalidPrice,
}: Props) => {
    if (!cart || !cart.isAwaitingApproval || cart.cartLines?.length === 0) {
        return null;
    }

    const productsCannotBePurchased = cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy);

    // xng-change: change the structure and add custom messages
    return (
        // <StyledWrapper {...styles.approvingWrapper}>
        //     <Typography {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
        //         {translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
        //     </Typography>
        // </StyledWrapper>

        <>
            {cart.isAwaitingApproval && (
                <Notify type="info" fullWidth data-test-selector="cart_approvingOrderMessage">
                    {translate("You are approving order #{0}", cart.orderNumber)}
                </Notify>
            )}
            {!!cart.quoteRequiredCount && (
                <Notify type="info" fullWidth data-test-selector="cart_rfqQuoteRequiredMessage">
                    {siteMessage("Rfq_QuoteRequiredMsg")}
                </Notify>
            )}

            {cart.properties["someItemsNotPriced"] === "true" && (
                <Notify type="warning" fullWidth data-test-selector="cart_itemsNotPricedMessage">
                    {isPunchOutOrder
                        ? siteMessage("STR_Punchout_Cart_Items_Not_Priced_Msg")
                        : siteMessage("STR_Cart_Items_Not_Priced_Msg")}
                </Notify>
            )}
            {cart.properties["hasBidItem"] === "true" && (
                <Notify type="warning" fullWidth data-test-selector="cart_preventPromotionsMessage">
                    {siteMessage("STR_Prevent_Promotions")}
                </Notify>
            )}
            {cart.hasInsufficientInventory && (
                <Notify type="danger" fullWidth data-test-selector="cart_insufficientInventoryAtCheckoutMessage">
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {cart.cartLines && currentMode === "Vmi" && isAuthenticated && isGuest && (
                <Notify type="warning" fullWidth data-test-selector="cart_viewOnVmiMessage">
                    {siteMessage("Cart_View_On_Vmi")}
                </Notify>
            )}
            {cart.failedToGetRealTimeInventory && (
                <Notify type="danger" fullWidth data-test-selector="cart_noInventoryAvailableAtCheckoutMessage">
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify type="danger" fullWidth data-test-selector="cart_productsCannotBePurchasedMessage">
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {!cart.cartNotPriced && hasInvalidPrice && (
                <Notify type="danger" fullWidth data-test-selector="cart_invalidPriceAtCheckoutMessage">
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
